import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Scale, RefreshCw, Building, DollarSign, CheckCircle2, AlertCircle, 
  ArrowRight, Link2, Unlink, Zap, Filter, Calendar, ChevronDown,
  AlertTriangle, TrendingUp, TrendingDown, Search, X, Check, Loader2,
  Plus, FileText, CheckSquare, Square, Layers
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Reconciliation() {
  // State
  const [bankAccounts, setBankAccounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [systemTransactions, setSystemTransactions] = useState([]);
  const [discrepancies, setDiscrepancies] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [adjustmentCategories, setAdjustmentCategories] = useState({ income: [], expense: [] });
  
  // Filters
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedBankAccount, setSelectedBankAccount] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showMatched, setShowMatched] = useState(false);
  const [systemTypeFilter, setSystemTypeFilter] = useState('');
  
  // Selection state for matching
  const [selectedBankTx, setSelectedBankTx] = useState(null);
  const [selectedSystemTxs, setSelectedSystemTxs] = useState([]);
  
  // Bulk selection state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedBulkTxs, setSelectedBulkTxs] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [activeTab, setActiveTab] = useState('match'); // 'match' | 'discrepancies'
  const [showFilters, setShowFilters] = useState(false);
  
  // Adjustment modal state
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentBankTx, setAdjustmentBankTx] = useState(null);
  const [adjustmentForm, setAdjustmentForm] = useState({
    category: '',
    description: '',
    notes: '',
    auto_match: true
  });
  const [creatingAdjustment, setCreatingAdjustment] = useState(false);
  
  // Bulk adjustment modal state
  const [showBulkAdjustmentModal, setShowBulkAdjustmentModal] = useState(false);
  const [bulkAdjustmentForm, setBulkAdjustmentForm] = useState({
    category: '',
    notes: '',
    auto_match: true
  });
  const [creatingBulkAdjustment, setCreatingBulkAdjustment] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch transactions when filters change
  useEffect(() => {
    if (selectedProperty) {
      fetchTransactions();
      fetchDiscrepancies();
    }
  }, [selectedProperty, selectedBankAccount, dateRange, showMatched, systemTypeFilter]);

  // Fetch suggestions when bank transaction selected
  useEffect(() => {
    if (selectedBankTx && selectedProperty) {
      fetchSuggestions(selectedBankTx.id);
    } else {
      setSuggestions([]);
    }
  }, [selectedBankTx, selectedProperty]);

  const fetchInitialData = async () => {
    try {
      const [propsRes, accRes, catRes] = await Promise.all([
        axios.get(`${API}/corporations`),
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/reconciliations/adjustment-categories`).catch(() => ({ data: { income: [], expense: [] } }))
      ]);
      setProperties(propsRes.data);
      setBankAccounts(accRes.data);
      setAdjustmentCategories(catRes.data);
      
      if (propsRes.data.length > 0) {
        setSelectedProperty(propsRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = useCallback(async () => {
    if (!selectedProperty) return;
    
    try {
      const params = new URLSearchParams();
      params.append('entity_id', selectedProperty);
      if (selectedBankAccount) params.append('bank_account_id', selectedBankAccount);
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      params.append('matched', showMatched);

      const [bankRes, sysRes] = await Promise.all([
        axios.get(`${API}/reconciliations/bank-transactions?${params}`),
        axios.get(`${API}/reconciliations/system-transactions?entity_id=${selectedProperty}${systemTypeFilter ? `&transaction_type=${systemTypeFilter}` : ''}&matched=${showMatched}${dateRange.start ? `&start_date=${dateRange.start}` : ''}${dateRange.end ? `&end_date=${dateRange.end}` : ''}`)
      ]);
      
      setBankTransactions(bankRes.data);
      setSystemTransactions(sysRes.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, [selectedProperty, selectedBankAccount, dateRange, showMatched, systemTypeFilter]);

  const fetchDiscrepancies = async () => {
    if (!selectedProperty) return;
    try {
      const params = new URLSearchParams({ entity_id: selectedProperty });
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const res = await axios.get(`${API}/reconciliations/discrepancies?${params}`);
      setDiscrepancies(res.data);
    } catch (error) {
      console.error('Failed to fetch discrepancies:', error);
    }
  };

  const fetchSuggestions = async (bankTxId) => {
    try {
      const res = await axios.get(`${API}/reconciliations/suggestions?entity_id=${selectedProperty}&bank_transaction_id=${bankTxId}`);
      setSuggestions(res.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const handleMatch = async () => {
    if (!selectedBankTx || selectedSystemTxs.length === 0) return;
    
    setMatching(true);
    try {
      await axios.post(`${API}/reconciliations/match`, {
        bank_transaction_id: selectedBankTx.id,
        system_transaction_ids: selectedSystemTxs.map(t => t.id)
      });
      
      // Reset selection and refresh
      setSelectedBankTx(null);
      setSelectedSystemTxs([]);
      await fetchTransactions();
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to match transactions:', error);
    } finally {
      setMatching(false);
    }
  };

  const handleUnmatch = async (bankTxId) => {
    try {
      await axios.post(`${API}/reconciliations/unmatch/${bankTxId}`);
      await fetchTransactions();
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to unmatch transactions:', error);
    }
  };

  const handleAutoMatch = async () => {
    if (!selectedProperty) return;
    
    setAutoMatching(true);
    try {
      const res = await axios.post(`${API}/reconciliations/auto-match`, {
        entity_id: selectedProperty,
        tolerance_days: 3,
        tolerance_amount: 0.01
      });
      
      alert(`Auto-matched ${res.data.matches_made} transactions`);
      await fetchTransactions();
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to auto-match:', error);
    } finally {
      setAutoMatching(false);
    }
  };

  // Open adjustment modal for a bank transaction
  const openAdjustmentModal = (bankTx) => {
    const amount = bankTx.type === 'expense' ? -bankTx.amount : bankTx.amount;
    setAdjustmentBankTx(bankTx);
    setAdjustmentForm({
      category: '',
      description: bankTx.description || '',
      notes: '',
      auto_match: true
    });
    setShowAdjustmentModal(true);
  };

  // Create adjustment from bank transaction
  const handleCreateAdjustment = async (e) => {
    e.preventDefault();
    if (!adjustmentBankTx || !adjustmentForm.category) return;
    
    setCreatingAdjustment(true);
    try {
      await axios.post(`${API}/reconciliations/create-adjustment`, {
        bank_transaction_id: adjustmentBankTx.id,
        category: adjustmentForm.category,
        description: adjustmentForm.description,
        notes: adjustmentForm.notes,
        auto_match: adjustmentForm.auto_match
      });
      
      setShowAdjustmentModal(false);
      setAdjustmentBankTx(null);
      await fetchTransactions();
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to create adjustment:', error);
      alert(error.response?.data?.detail || 'Failed to create adjustment');
    } finally {
      setCreatingAdjustment(false);
    }
  };

  // Toggle bulk selection for a bank transaction
  const toggleBulkSelection = (tx) => {
    setSelectedBulkTxs(prev => {
      const exists = prev.find(t => t.id === tx.id);
      if (exists) {
        return prev.filter(t => t.id !== tx.id);
      }
      return [...prev, tx];
    });
  };

  // Select all unmatched bank transactions
  const selectAllUnmatched = () => {
    const unmatched = bankTransactions.filter(tx => !tx.matched);
    setSelectedBulkTxs(unmatched);
  };

  // Clear bulk selection
  const clearBulkSelection = () => {
    setSelectedBulkTxs([]);
    setBulkSelectMode(false);
  };

  // Open bulk adjustment modal
  const openBulkAdjustmentModal = () => {
    if (selectedBulkTxs.length === 0) return;
    setBulkAdjustmentForm({
      category: '',
      notes: '',
      auto_match: true
    });
    setShowBulkAdjustmentModal(true);
  };

  // Create bulk adjustments
  const handleCreateBulkAdjustment = async (e) => {
    e.preventDefault();
    if (selectedBulkTxs.length === 0 || !bulkAdjustmentForm.category) return;
    
    setCreatingBulkAdjustment(true);
    try {
      const res = await axios.post(`${API}/reconciliations/bulk-adjustment`, {
        bank_transaction_ids: selectedBulkTxs.map(tx => tx.id),
        category: bulkAdjustmentForm.category,
        notes: bulkAdjustmentForm.notes,
        auto_match: bulkAdjustmentForm.auto_match
      });
      
      alert(`Created ${res.data.created} adjustments${res.data.errors > 0 ? `, ${res.data.errors} errors` : ''}`);
      setShowBulkAdjustmentModal(false);
      setSelectedBulkTxs([]);
      setBulkSelectMode(false);
      await fetchTransactions();
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Failed to create bulk adjustments:', error);
      alert(error.response?.data?.detail || 'Failed to create bulk adjustments');
    } finally {
      setCreatingBulkAdjustment(false);
    }
  };

  const toggleSystemTxSelection = (tx) => {
    setSelectedSystemTxs(prev => {
      const exists = prev.find(t => t.id === tx.id);
      if (exists) {
        return prev.filter(t => t.id !== tx.id);
      }
      return [...prev, tx];
    });
  };

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('en-CA', { 
      style: 'currency', 
      currency: 'CAD',
      minimumFractionDigits: 2 
    }).format(Math.abs(amount || 0));
    return amount < 0 ? `-${formatted}` : formatted;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-CA');
  };

  const getTypeColor = (type) => {
    const colors = {
      collection: 'bg-emerald-100 text-emerald-700',
      invoice: 'bg-blue-100 text-blue-700',
      payroll: 'bg-purple-100 text-purple-700',
      transaction: 'bg-slate-100 text-slate-700'
    };
    return colors[type] || colors.transaction;
  };

  const selectedTotal = selectedSystemTxs.reduce((sum, tx) => sum + tx.amount, 0);
  const bankTxAmount = selectedBankTx ? (selectedBankTx.type === 'expense' ? -selectedBankTx.amount : selectedBankTx.amount) : 0;
  const matchDifference = bankTxAmount - selectedTotal;
  const bulkTotal = selectedBulkTxs.reduce((sum, tx) => {
    const amt = tx.type === 'expense' ? -tx.amount : tx.amount;
    return sum + amt;
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reconciliation-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bank Reconciliation</h1>
          <p className="text-slate-500">Match bank transactions with system records</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          <button
            onClick={() => {
              setBulkSelectMode(!bulkSelectMode);
              if (bulkSelectMode) {
                setSelectedBulkTxs([]);
              }
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${
              bulkSelectMode 
                ? 'bg-indigo-600 text-white hover:bg-indigo-700' 
                : 'border border-slate-300 hover:bg-slate-50'
            }`}
            data-testid="bulk-select-btn"
          >
            <Layers className="w-4 h-4" />
            {bulkSelectMode ? 'Exit Bulk Mode' : 'Bulk Select'}
          </button>
          <button
            onClick={handleAutoMatch}
            disabled={autoMatching || !selectedProperty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
            data-testid="auto-match-btn"
          >
            {autoMatching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Auto-Match
          </button>
          <button
            onClick={() => { fetchTransactions(); fetchDiscrepancies(); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg hover:bg-slate-200"
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Bulk Selection Bar */}
      {bulkSelectMode && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="bulk-selection-bar">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm text-indigo-700">
                <strong>{selectedBulkTxs.length}</strong> transactions selected
                {selectedBulkTxs.length > 0 && (
                  <span className="ml-2">
                    (Total: <strong className={bulkTotal >= 0 ? 'text-emerald-600' : 'text-rose-600'}>
                      {formatCurrency(bulkTotal)}
                    </strong>)
                  </span>
                )}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={selectAllUnmatched}
                className="px-3 py-1.5 text-sm border border-indigo-300 rounded-lg hover:bg-indigo-100 text-indigo-700"
                data-testid="select-all-unmatched-btn"
              >
                Select All Unmatched
              </button>
              <button
                onClick={clearBulkSelection}
                className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                data-testid="clear-bulk-selection-btn"
              >
                Clear Selection
              </button>
              <button
                onClick={openBulkAdjustmentModal}
                disabled={selectedBulkTxs.length === 0}
                className="px-4 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="bulk-adjust-btn"
              >
                <FileText className="w-4 h-4" />
                Bulk Adjust ({selectedBulkTxs.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="filters-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="property-filter"
              >
                <option value="">All Properties</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bank Account</label>
              <select
                value={selectedBankAccount}
                onChange={(e) => setSelectedBankAccount(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="bank-account-filter"
              >
                <option value="">All Accounts</option>
                {bankAccounts.filter(a => !selectedProperty || a.entity_id === selectedProperty).map(a => (
                  <option key={a.id} value={a.id}>{a.account_name} - {a.bank_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="start-date-filter"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="end-date-filter"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Show</label>
              <select
                value={showMatched}
                onChange={(e) => setShowMatched(e.target.value === 'true')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                data-testid="matched-filter"
              >
                <option value="false">Unmatched Only</option>
                <option value="true">Matched Only</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {discrepancies && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="bank-transactions-card">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Building className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{discrepancies.unmatched_bank_transactions}</p>
                <p className="text-sm text-slate-500">Unmatched Bank Txns</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="system-items-card">
            <div className="flex items-center gap-3">
              <div className="bg-violet-100 p-2 rounded-lg">
                <Scale className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-violet-600">{discrepancies.unmatched_system_count}</p>
                <p className="text-sm text-slate-500">Unmatched System Items</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="bank-total-card">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(discrepancies.unmatched_bank_total)}</p>
                <p className="text-sm text-slate-500">Bank Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="discrepancy-card">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${Math.abs(discrepancies.net_discrepancy) < 1 ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${Math.abs(discrepancies.net_discrepancy) < 1 ? 'text-emerald-600' : 'text-amber-600'}`} />
              </div>
              <div>
                <p className={`text-lg font-bold ${Math.abs(discrepancies.net_discrepancy) < 1 ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {formatCurrency(discrepancies.net_discrepancy)}
                </p>
                <p className="text-sm text-slate-500">Net Discrepancy</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('match')}
          className={`px-4 py-2 font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'match' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="match-tab"
        >
          Transaction Matching
        </button>
        <button
          onClick={() => setActiveTab('discrepancies')}
          className={`px-4 py-2 font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'discrepancies' 
              ? 'border-indigo-600 text-indigo-600' 
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          data-testid="discrepancies-tab"
        >
          Discrepancies
        </button>
      </div>

      {/* Match Tab Content */}
      {activeTab === 'match' && (
        <>
          {/* Matching Status Bar */}
          {selectedBankTx && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="matching-status-bar">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-sm">
                    <span className="text-slate-500">Bank Transaction:</span>
                    <span className="ml-2 font-medium text-slate-900">{selectedBankTx.description}</span>
                    <span className={`ml-2 font-bold ${bankTxAmount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(bankTxAmount)}
                    </span>
                  </div>
                  <ArrowRight className="w-5 h-5 text-indigo-400 hidden lg:block" />
                  <div className="text-sm">
                    <span className="text-slate-500">Selected ({selectedSystemTxs.length}):</span>
                    <span className={`ml-2 font-bold ${selectedTotal >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {formatCurrency(selectedTotal)}
                    </span>
                  </div>
                  {selectedSystemTxs.length > 0 && (
                    <div className="text-sm">
                      <span className="text-slate-500">Difference:</span>
                      <span className={`ml-2 font-bold ${Math.abs(matchDifference) < 0.01 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {formatCurrency(matchDifference)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setSelectedBankTx(null); setSelectedSystemTxs([]); }}
                    className="px-3 py-1 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
                    data-testid="cancel-match-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMatch}
                    disabled={matching || selectedSystemTxs.length === 0}
                    className="px-4 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
                    data-testid="confirm-match-btn"
                  >
                    {matching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    Match Selected
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Two Panel Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bank Transactions Panel */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="bank-transactions-panel">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">Bank Transactions</h3>
                  <span className="text-sm text-slate-500">{bankTransactions.length} items</span>
                </div>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {bankTransactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Building className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No bank transactions found</p>
                    <p className="text-sm mt-1">Import a bank statement to get started</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {bankTransactions.map(tx => {
                      const amount = tx.type === 'expense' ? -tx.amount : tx.amount;
                      const isSelected = selectedBankTx?.id === tx.id;
                      const isMatched = tx.matched;
                      const isBulkSelected = selectedBulkTxs.find(t => t.id === tx.id);
                      
                      return (
                        <div
                          key={tx.id}
                          onClick={() => {
                            if (bulkSelectMode && !isMatched) {
                              toggleBulkSelection(tx);
                            } else if (!isMatched) {
                              setSelectedBankTx(isSelected ? null : tx);
                            }
                          }}
                          className={`p-4 cursor-pointer transition-colors ${
                            isBulkSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' :
                            isSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' : 
                            isMatched ? 'bg-emerald-50 opacity-75' : 'hover:bg-slate-50'
                          }`}
                          data-testid={`bank-tx-${tx.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              {/* Bulk select checkbox */}
                              {bulkSelectMode && !isMatched && (
                                <div 
                                  className="mt-0.5"
                                  onClick={(e) => { e.stopPropagation(); toggleBulkSelection(tx); }}
                                >
                                  {isBulkSelected ? (
                                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                                  ) : (
                                    <Square className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-slate-900 truncate">{tx.description}</p>
                                  {isMatched && (
                                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                      <CheckCircle2 className="w-3 h-3" /> Matched
                                    </span>
                                  )}
                                  {isBulkSelected && (
                                    <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                      <Check className="w-3 h-3" /> Selected
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-500 mt-1">{formatDate(tx.date)}</p>
                                {isMatched && tx.matched_details && (
                                  <div className="mt-2 text-xs text-slate-500">
                                    Matched to: {tx.matched_details.map(d => d.description || d.id).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`font-semibold ${amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {formatCurrency(amount)}
                              </span>
                              {isMatched ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleUnmatch(tx.id); }}
                                  className="p-1 hover:bg-rose-100 rounded text-rose-500"
                                  title="Unmatch"
                                  data-testid={`unmatch-${tx.id}`}
                                >
                                  <Unlink className="w-4 h-4" />
                                </button>
                              ) : !bulkSelectMode && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openAdjustmentModal(tx); }}
                                  className="p-1.5 hover:bg-indigo-100 rounded text-indigo-600 border border-indigo-200"
                                  title="Create Adjustment"
                                  data-testid={`create-adjustment-${tx.id}`}
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* System Transactions Panel */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="system-transactions-panel">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-900">System Transactions</h3>
                  <div className="flex items-center gap-2">
                    <select
                      value={systemTypeFilter}
                      onChange={(e) => setSystemTypeFilter(e.target.value)}
                      className="text-sm border border-slate-300 rounded px-2 py-1"
                      data-testid="system-type-filter"
                    >
                      <option value="">All Types</option>
                      <option value="collection">Collections</option>
                      <option value="invoice">Invoices</option>
                      <option value="payroll">Payroll</option>
                      <option value="transaction">Transactions</option>
                    </select>
                    <span className="text-sm text-slate-500">{systemTransactions.length} items</span>
                  </div>
                </div>
              </div>
              
              {/* Suggestions Section */}
              {selectedBankTx && suggestions.length > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 p-3">
                  <p className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4" /> Suggested Matches
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.slice(0, 5).map(s => (
                      <button
                        key={s.id}
                        onClick={() => {
                          const tx = systemTransactions.find(t => t.id === s.id);
                          if (tx && !selectedSystemTxs.find(t => t.id === s.id)) {
                            setSelectedSystemTxs(prev => [...prev, tx]);
                          }
                        }}
                        className="text-xs px-2 py-1 bg-white border border-amber-300 rounded-full hover:bg-amber-100"
                        data-testid={`suggestion-${s.id}`}
                      >
                        {s.description?.substring(0, 30)} • {formatCurrency(s.amount)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="max-h-[500px] overflow-y-auto">
                {systemTransactions.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Scale className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No system transactions found</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {systemTransactions.map(tx => {
                      const isSelected = selectedSystemTxs.find(t => t.id === tx.id);
                      const isReconciled = tx.reconciled;
                      
                      return (
                        <div
                          key={tx.id}
                          onClick={() => selectedBankTx && !isReconciled && toggleSystemTxSelection(tx)}
                          className={`p-4 transition-colors ${
                            selectedBankTx ? 'cursor-pointer' : ''
                          } ${
                            isSelected ? 'bg-indigo-50 border-l-4 border-indigo-500' :
                            isReconciled ? 'bg-emerald-50 opacity-75' : 
                            selectedBankTx ? 'hover:bg-slate-50' : ''
                          }`}
                          data-testid={`system-tx-${tx.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(tx.type)}`}>
                                  {tx.type}
                                </span>
                                {isReconciled && (
                                  <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                    <CheckCircle2 className="w-3 h-3" /> Reconciled
                                  </span>
                                )}
                                {isSelected && (
                                  <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                    <Check className="w-3 h-3" /> Selected
                                  </span>
                                )}
                              </div>
                              <p className="font-medium text-slate-900 mt-1 truncate">{tx.description}</p>
                              <p className="text-sm text-slate-500">{formatDate(tx.date)} • {tx.source}</p>
                            </div>
                            <span className={`font-semibold ${tx.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                              {formatCurrency(tx.amount)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Discrepancies Tab Content */}
      {activeTab === 'discrepancies' && discrepancies && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="discrepancies-content">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Reconciliation Discrepancies</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Bank Side */}
            <div>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-500" />
                Unmatched Bank Transactions ({discrepancies.by_type.bank_transactions})
              </h4>
              <div className="space-y-2">
                {discrepancies.unmatched_bank_items?.map(item => (
                  <div key={item.id} className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{item.description}</p>
                        <p className="text-xs text-slate-500">{formatDate(item.date)}</p>
                      </div>
                      <span className={`font-semibold text-sm ${item.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  </div>
                ))}
                {discrepancies.unmatched_bank_items?.length === 0 && (
                  <p className="text-sm text-slate-500">No unmatched bank transactions</p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-700">Bank Total:</span>
                  <span className="font-bold text-blue-600">{formatCurrency(discrepancies.unmatched_bank_total)}</span>
                </div>
              </div>
            </div>

            {/* System Side */}
            <div>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-violet-500" />
                Unmatched System Items ({discrepancies.unmatched_system_count})
              </h4>
              <div className="space-y-2">
                {discrepancies.unmatched_system_items?.map(item => (
                  <div key={item.id} className="p-3 bg-violet-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <p className="font-medium text-slate-900 text-sm mt-1">{item.description}</p>
                        <p className="text-xs text-slate-500">{formatDate(item.date)}</p>
                      </div>
                      <span className={`font-semibold text-sm ${item.amount >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {formatCurrency(item.amount)}
                      </span>
                    </div>
                  </div>
                ))}
                {discrepancies.unmatched_system_items?.length === 0 && (
                  <p className="text-sm text-slate-500">No unmatched system items</p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex justify-between">
                  <span className="font-medium text-slate-700">System Total:</span>
                  <span className="font-bold text-violet-600">{formatCurrency(discrepancies.unmatched_system_total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Discrepancy */}
          <div className={`mt-6 p-4 rounded-xl ${Math.abs(discrepancies.net_discrepancy) < 1 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {Math.abs(discrepancies.net_discrepancy) < 1 ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                )}
                <div>
                  <p className="font-medium text-slate-900">Net Discrepancy</p>
                  <p className="text-sm text-slate-500">
                    {Math.abs(discrepancies.net_discrepancy) < 1 
                      ? 'All amounts are balanced' 
                      : 'There are differences between bank and system records'}
                  </p>
                </div>
              </div>
              <span className={`text-2xl font-bold ${Math.abs(discrepancies.net_discrepancy) < 1 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {formatCurrency(discrepancies.net_discrepancy)}
              </span>
            </div>
          </div>

          {/* Breakdown by Type */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h4 className="font-medium text-slate-700 mb-3">Unmatched Items by Type</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-700">{discrepancies.by_type.bank_transactions}</p>
                <p className="text-sm text-slate-500">Bank Txns</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-600">{discrepancies.by_type.collections}</p>
                <p className="text-sm text-slate-500">Collections</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{discrepancies.by_type.invoices}</p>
                <p className="text-sm text-slate-500">Invoices</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-500">{discrepancies.by_type.system_transactions}</p>
                <p className="text-sm text-slate-500">Other Txns</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Adjustment Modal */}
      {showAdjustmentModal && adjustmentBankTx && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="adjustment-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Create Adjustment</h3>
                <button 
                  onClick={() => setShowAdjustmentModal(false)}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              {/* Bank Transaction Info */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-500 mb-1">Bank Transaction</p>
                <p className="font-medium text-slate-900">{adjustmentBankTx.description}</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-slate-500">{formatDate(adjustmentBankTx.date)}</span>
                  <span className={`font-bold ${
                    (adjustmentBankTx.type === 'expense' ? -adjustmentBankTx.amount : adjustmentBankTx.amount) >= 0 
                      ? 'text-emerald-600' 
                      : 'text-rose-600'
                  }`}>
                    {formatCurrency(adjustmentBankTx.type === 'expense' ? -adjustmentBankTx.amount : adjustmentBankTx.amount)}
                  </span>
                </div>
              </div>

              <form onSubmit={handleCreateAdjustment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={adjustmentForm.category}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    data-testid="adjustment-category"
                  >
                    <option value="">Select category...</option>
                    {adjustmentBankTx.type === 'expense' ? (
                      <>
                        <optgroup label="Expense Categories">
                          {adjustmentCategories.expense.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Income Categories (if needed)">
                          {adjustmentCategories.income.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                      </>
                    ) : (
                      <>
                        <optgroup label="Income Categories">
                          {adjustmentCategories.income.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Expense Categories (if needed)">
                          {adjustmentCategories.expense.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                          ))}
                        </optgroup>
                      </>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={adjustmentForm.description}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter description"
                    data-testid="adjustment-description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    value={adjustmentForm.notes}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Optional notes..."
                    data-testid="adjustment-notes"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="auto_match"
                    checked={adjustmentForm.auto_match}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, auto_match: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    data-testid="adjustment-auto-match"
                  />
                  <label htmlFor="auto_match" className="text-sm text-slate-700">
                    Automatically match to this bank transaction
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAdjustmentModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                    data-testid="adjustment-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingAdjustment || !adjustmentForm.category}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="adjustment-submit"
                  >
                    {creatingAdjustment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4" />
                    )}
                    Create Adjustment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Adjustment Modal */}
      {showBulkAdjustmentModal && selectedBulkTxs.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="bulk-adjustment-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Bulk Create Adjustments</h3>
                <button 
                  onClick={() => setShowBulkAdjustmentModal(false)}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              
              {/* Selected Transactions Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-slate-500 mb-2">Creating adjustments for:</p>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-900">{selectedBulkTxs.length} transactions</span>
                  <span className={`font-bold ${bulkTotal >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatCurrency(bulkTotal)}
                  </span>
                </div>
                <div className="mt-2 max-h-32 overflow-y-auto">
                  {selectedBulkTxs.slice(0, 5).map(tx => (
                    <div key={tx.id} className="text-xs text-slate-500 py-1 flex justify-between">
                      <span className="truncate pr-2">{tx.description}</span>
                      <span>{formatCurrency(tx.type === 'expense' ? -tx.amount : tx.amount)}</span>
                    </div>
                  ))}
                  {selectedBulkTxs.length > 5 && (
                    <div className="text-xs text-slate-400 py-1">
                      ... and {selectedBulkTxs.length - 5} more
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleCreateBulkAdjustment} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Category for All <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={bulkAdjustmentForm.category}
                    onChange={(e) => setBulkAdjustmentForm(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                    data-testid="bulk-adjustment-category"
                  >
                    <option value="">Select category...</option>
                    <optgroup label="Expense Categories">
                      {adjustmentCategories.expense.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Income Categories">
                      {adjustmentCategories.income.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes (for all)</label>
                  <textarea
                    value={bulkAdjustmentForm.notes}
                    onChange={(e) => setBulkAdjustmentForm(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows={2}
                    placeholder="Optional notes for all adjustments..."
                    data-testid="bulk-adjustment-notes"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="bulk_auto_match"
                    checked={bulkAdjustmentForm.auto_match}
                    onChange={(e) => setBulkAdjustmentForm(prev => ({ ...prev, auto_match: e.target.checked }))}
                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    data-testid="bulk-adjustment-auto-match"
                  />
                  <label htmlFor="bulk_auto_match" className="text-sm text-slate-700">
                    Automatically match adjustments to bank transactions
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowBulkAdjustmentModal(false)}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                    data-testid="bulk-adjustment-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingBulkAdjustment || !bulkAdjustmentForm.category}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="bulk-adjustment-submit"
                  >
                    {creatingBulkAdjustment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Layers className="w-4 h-4" />
                    )}
                    Create {selectedBulkTxs.length} Adjustments
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
